import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { confirmObject, ConfirmsStatusUpdateResponse } from '../../models/confirms.model';
import { BannerSize, BannerVariant } from '@vg-constellation/angular-19/banner';
import { ModalDialogComponent } from '@vg-constellation/angular-19/modal-dialog';
import { Observable, Subscription } from 'rxjs';
import { ConfirmService } from '../../services/confirm/confirm.service';
import { CookieService } from 'ngx-cookie-service';
import { StatusUpdateDataTransferService } from '../../services/confirm/status-update-data-transfer.service';
import { CacheConfirmService } from '../../services/confirm/cache-confirm.service';
import { PlatformLocation } from '@angular/common';
import { AVAILABILITY } from '../../util/app.constants';
import { UpdateConfirmationStatus } from '../../models/update-confirmation-status.model';
import { environment } from '../../../environments/environment';
import { CaasService } from '../../services/caas/caas.service';

@Component({
  selector: 'app-status-review',
  templateUrl: './status-review.component.html',
  styleUrl: './status-review.component.scss',
  standalone: false
})
export class StatusReviewComponent implements OnInit, OnDestroy {
  changedConfirms$: confirmObject[] = [];
  sizeOfSelected: number = 0;
  isSubmitButtenDisabled: boolean = false
  @ViewChild('contentElement') elRef: ElementRef;
  isLoaderIndicator: boolean = true;
  response: ConfirmsStatusUpdateResponse = {} as ConfirmsStatusUpdateResponse;
  isError: boolean = false;
  errorMessage: string = '';
  shouldShowWarningForDelete: boolean = false;
  errorConfig: {size: BannerSize, variant: BannerVariant, headingText: string, body: string}= {
    size: 'medium',
    variant: 'warning',
    headingText: '',
    body: '',
  }
  @ViewChild('successModal') successModal: ModalDialogComponent;
  @ViewChild('errorModal') errorModal: ModalDialogComponent;
  component: { openModalDialog: (event: Event) => void; };
  subscription: Subscription;
  content: any;
  constructor(
    private readonly confirmService: ConfirmService,
    private readonly cookieService: CookieService,
    private readonly dataService: StatusUpdateDataTransferService,
    private readonly cache: CacheConfirmService,
    private readonly location: PlatformLocation,
    private readonly caasService: CaasService
  ) {
    this.isLoaderIndicator = false; // on component initialization
  }
  /**
   *Before moving away from current component it will show confirmation message
   */
  // canDeactivate: () => boolean | Observable<boolean> | Promise<boolean> = ():boolean => {
  //
  //   if (!this.cache.isEditEnable && this.cache.storeSelectedStatement.size>0) {
  //     const conf: boolean = window.confirm('Selected data will be lost. Do you want to continue?')
  //     if(conf) {
  //       window.location.href = '/'
  //       return false; // Prevent default navigation since we're handling it manually
  //     } else {
  //       return false; // Stay on the current page
  //     }
  //   }
  //   if (!this.cache.isEditEnable && this.cache.storeSelectedStatement.size === 0) {
  //     window.location.href = '/'
  //     return false;
  //   }
  //   return true; // Allow navigation if no data changed
  // }


  ngOnInit() {
    this.content = this.caasService.getPageContent()?.confirmationJourneyContent;
    this.subscription = this.dataService.data$.subscribe(data => {
      this.changedConfirms$ = data || [];

      // If no data is available (e.g., page refresh), redirect to home
      if (!data || data.length === 0) {
        window.document.body.hidden = false;
        window.location.href = '/';
        return;
      }

      this.sizeOfSelected = this.changedConfirms$.length;
      this.hasDeleteConfirmation(this.changedConfirms$);
    });
  }

  /**
   * If user want to update confirmation to delete should render warning message.
   * @param confirms confirmations
   */
  hasDeleteConfirmation(confirms: confirmObject[]) {
    this.shouldShowWarningForDelete = confirms.some(value => value.newStatus?.toLowerCase() === AVAILABILITY.DELETE.toLowerCase())
    if(this.shouldShowWarningForDelete) {
      this.errorConfig.headingText = this.content?.statusReviewMessages?.deletePermanentHeading || 'Deleting a confirmation is permanent'
      this.errorConfig.body = this.content?.statusReviewMessages?.deletePermanentBody || 'You will not be able to change the status again.'
    }
  }

  /**
   * Selected confirmations will be updated.
   * @param confirms need to be updated.
   * @param event event
   */
  async finalSubmit(confirms: confirmObject[], event: any) {
    this.isLoaderIndicator = true;
    this.isSubmitButtenDisabled = true
    try {
      const confirmations: UpdateConfirmationStatus[] = confirms.map(confirm => ({
        confirmationId: confirm.confirmationId,
        availabilityStatus: confirm.newStatus || ''
      }));

      this.response = await this.confirmService.updateConfirmationStatus(confirmations);

      this.isLoaderIndicator = false;
      this.isSubmitButtenDisabled = false
      this.successModal.openModalDialog(event);
    } catch (error: any) {
      if (error.status === 0) {
        // Network error or CORS issue
        this.errorMessage = 'Network error'
        console.error('Network error:', error.message);
      } else if (error.status >= 500) {
        // Server-side error
        this.errorMessage = 'Server error'
        console.error('Server error:', error?.error?.errorMessage || error?.error?.message || 'Server error');
      } else if (error.status >= 400) {
        // Client-side error or custom error from backend
        this.errorMessage = error?.error?.message || error?.error?.errorMessage || 'Client error occurred';
        console.error('Custom error:', this.errorMessage);
      } else {
        // Unknown error
        this.errorMessage = 'Unexpected error'
        console.error('Unexpected error:', error);
      }
      this.isError = true
      this.isLoaderIndicator = false;
      this.isSubmitButtenDisabled = false
      this.errorModal.openModalDialog(event);
    }
  }

  /**
   * when user click on close button it will redirect to home page.
   */
  async closeButton() {
    this.cache.storeSelectedConfirms.clear();
    this.cache.masterCache.clear();
    this.cache.isEditEnable = false;
    window.location.href = '/'
  }

  /**
   * When user click on edit button it will navigate to previous page
   */
  async edit() {
    this.cache.isEditEnable = true;
    this.location.back()
  }

  /**
   * if new status is deleted at that time it highlight the rows.
   * @param confirmation confirmation
   */
  isDeleteStatus(confirmation: confirmObject): string {
    return confirmation.newStatus?.toLowerCase() === AVAILABILITY.DELETE.toLowerCase() ? 'tr__highlight_delete' : '';
  }

  /**
   * Destroy all the subscribe data before component destroys
   */
  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  protected readonly environment = environment;
}
